/**
 * Image Classification Domain Models
 * Shared types for AI image classification and metadata handling
 */

export type ClassificationType = 'fuse_cutout' | 'consumer_unit' | 'isolator_switch';

export type AnalysisScope = 'quality' | 'classification';

export type AnalysisResponseStatus = 'success' | 'quality_too_low' | 'error';

export type ImageClassificationTarget = 'fuse-cutout' | 'consumer-unit' | 'isolator-switch';

const CLASSIFICATION_TYPE_BY_TARGET = {
  'fuse-cutout': 'fuse_cutout',
  'consumer-unit': 'consumer_unit',
  'isolator-switch': 'isolator_switch'
} as const satisfies Record<ImageClassificationTarget, ClassificationType>;

export function classificationTypeOf(target: string | undefined): ClassificationType | undefined {
  const entry = Object.entries(CLASSIFICATION_TYPE_BY_TARGET).find(([key]) => key === target);
  return entry?.[1];
}

/**
 * Request payload for image classification API
 */
export interface ImageClassificationRequest {
  image: string;
  classificationType?: ClassificationType;
  projectId?: string;
  analysisScope?: AnalysisScope;
}

/**
 * Parameters accepted by ImageClassificationService.classifyImage
 */
export interface ClassifyImageParams {
  base64Image: string;
  deviceContext?: 'mobile' | 'desktop';
  options?: ClassificationOptions;
  classificationType?: ClassificationType;
  projectId?: string;
  analysisScope?: AnalysisScope;
}

export type Outcome = 'success' | 'qualityIssues' | 'lowConfidence' | 'wrongEquipment' | 'noDetection' | 'error';

export type AdviceSeverity = 'success' | 'info' | 'warning' | 'error';

export interface Advice {
  readonly code: string;
  readonly severity: AdviceSeverity;
  readonly params: Readonly<Record<string, string | number>>;
}

export interface ImageClassificationError {
  detail?: string;
  status?: number;
  title?: string;
  error?: string;
  analysisId?: string;
  outcome?: Outcome;
  advice?: Advice[];
}

/**
 * Detection object from the API
 */
export interface Detection {
  bbox: [number, number, number, number]; // [x1, y1, x2, y2]
  label: string;
  confidence: number; // Percentage (0-100)
}

/**
 * Options for configuring classification behavior
 */
export interface ClassificationOptions {
  enabled?: boolean;
  autoPopulateForm?: boolean;
  confidenceThreshold?: number;
  minResolution?: number;
  outputQuality?: number;
  includeProcessedImage?: boolean;
  minBlurScore?: number;
  minExposureScore?: number;
  timeout?: number;
  showResults?: boolean;
  additionalOptions?: { [key: string]: unknown };
}

/**
 * Quality assessment details
 */
export interface QualityDetails {
  // Core scores
  resolution_score: number;
  blur_score: number;
  exposure_score: number;
  is_acceptable: boolean;
  issues: string[];

  // Threshold values
  resolution_score_threshold?: number;
  blur_score_threshold?: number;
  exposure_score_threshold?: number;

  // Threshold met flags
  resolution_score_threshold_met?: boolean;
  blur_score_threshold_met?: boolean;
  exposure_score_threshold_met?: boolean;

  // Megapixels
  megapixels?: number;
  megapixels_threshold?: number;
  megapixels_threshold_met?: boolean;

  // Overall quality
  overall_quality?: number;
  overall_quality_threshold?: number;
  overall_quality_threshold_met?: boolean;
}

/**
 * Simplified classification result for UI consumption and S3 metadata
 */
export interface ClassificationResult {
  status?: AnalysisResponseStatus;
  analysisId?: string;
  analysisScope?: AnalysisScope;
  outcome?: Outcome;
  advice?: Advice[];
  success: boolean;
  equipmentType: string;
  confidence: number;
  fuseCutoutConfidence: number;
  consumerUnitConfidence: number;
  isolatorSwitchConfidence: number;
  classConfidences?: Record<string, number> | null;
  confidenceThresholdMet: boolean;
  qualityScore: number;
  qualityAcceptable: boolean;
  qualityIssues: string[];
  qualityDetails?: QualityDetails;
  warnings: string[];
  error?: string;
  errorDetails?: string[];
  boundingBoxes?: Array<{
    label: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    confidence: number;
  }>;
  detectionSettings?: {
    conf: number;
    iou: number;
    img: number;
    expand: number;
  };
  imageWidth?: number;
  imageHeight?: number;
  sourceWidth?: number;
  sourceHeight?: number;
}

export interface ProjectAnalysis extends ClassificationResult {
  assetKey: string;
  questionKey?: string;
}

export interface ProjectAnalysesResponse {
  analyses: ProjectAnalysis[];
}

// Maps each classification type to its confidence field. `satisfies` keeps it exhaustive: a new
// ClassificationType with no entry, or a bad field name, is a compile error.
export const CLASSIFICATION_CONFIDENCE_FIELDS = {
  fuse_cutout: 'fuseCutoutConfidence',
  consumer_unit: 'consumerUnitConfidence',
  isolator_switch: 'isolatorSwitchConfidence'
} as const satisfies Record<ClassificationType, keyof ClassificationResult>;

/**
 * Image with the id of the analysis captured for it
 * Used when passing classified images through the form submission flow
 */
export interface ImageWithMetadata {
  imageData: string; // base64 encoded image string
  analysisId?: string;
}

/**
 * Type guard to check if a value is an ImageWithMetadata object
 */
export function isImageWithMetadata(value: unknown): value is ImageWithMetadata {
  return value !== null && typeof value === 'object' && 'imageData' in value && typeof value.imageData === 'string';
}
